import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  ApproverItem,
  approveRejectSelfTransfer,
  fetchSelfTransferDetails,
  formatDateDisplay,
  getApproverEmpId,
  groundLabel,
  parseApprovers,
  reasonLabel,
  resolveStatusBadge,
  syncSelfTransferRequests,
} from "./selfTransfer.service";

const InfoRow: React.FC<{ label: string; value?: React.ReactNode; hideEmpty?: boolean }> = ({
  label,
  value,
  hideEmpty,
}) => {
  if (hideEmpty && (value === undefined || value === null || value === "")) return null;
  return (
    <div className="col-md-4 col-sm-6">
      <label className="form-label text-muted small mb-1">{label}</label>
      <div className="fw-semibold text-dark">{value || "-"}</div>
    </div>
  );
};

const SelfTransferApproval: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const workItemId = queryParams.get("workItemId");
  const requestID = id || "";

  const [details, setDetails] = useState<any>(null);
  const [approvers, setApprovers] = useState<ApproverItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Take Action Fields
  const [nodalRemarks, setNodalRemarks] = useState("");
  const [specialComment, setSpecialComment] = useState("");
  const [comment, setComment] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  // Confirmation Modal state
  const [confirmModalAction, setConfirmModalAction] = useState<"A" | "R" | null>(null);

  const fetchDetails = useCallback(async () => {
    if (!requestID) return;
    try {
      setLoading(true);
      const res = await fetchSelfTransferDetails(String(requestID));
      if (res.success && res.data) {
        const d = res.data;
        setDetails(d);

        const currentApproverEmpId = getApproverEmpId();
        const myRemark = Array.isArray(d.remarks)
          ? d.remarks.find((r: any) => String(r.ApproverEmpID) === String(currentApproverEmpId))
          : null;

        if (d.SpecialComment || myRemark?.SpecialComment) {
          setSpecialComment(d.SpecialComment || myRemark?.SpecialComment || "");
        }
        if (d.NodalRemarksRegardingAvailability || myRemark?.NodalRemarksRegardingAvailability) {
          setNodalRemarks(
            d.NodalRemarksRegardingAvailability || myRemark?.NodalRemarksRegardingAvailability || ""
          );
        }
        if (myRemark?.ApproverComment) {
          setComment(myRemark.ApproverComment || "");
        }

        setApprovers(parseApprovers(d));
      } else {
        toast.error("Failed to load self transfer request details");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to load request details");
    } finally {
      setLoading(false);
    }
  }, [requestID]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleAction = async (action: "A" | "R") => {
    if (!acknowledged) {
      toast.warning("Please check the acknowledgement checkbox before proceeding.");
      return;
    }

    const approverEmpId = getApproverEmpId();
    const payload = {
      Action: action,
      RequestID: String(requestID),
      EmpID: String(details?.EmpID || ""),
      ApproverID: approverEmpId,
      Check: acknowledged ? "X" : "",
      SpecialComment: specialComment.trim(),
      ApproverComment: comment.trim(),
      NodalRemarksRegardingAvailability: nodalRemarks.trim(),
    };

    try {
      setSubmitting(true);
      const res = await approveRejectSelfTransfer(payload);
      if (res.success) {
        toast.success(
          action === "A" ? "Application has been approved" : "Application has been rejected"
        );
        // Best-effort sync
        try {
          await syncSelfTransferRequests(details?.EmpID);
        } catch {
          /* ignore */
        }
        setConfirmModalAction(null);
        navigate(routes.todoWorklist.tasks);
      } else {
        toast.error(res.message || "Action failed");
      }
    } catch (err: any) {
      toast.error(err?.message || "An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !details) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-danger" style={{ color: "#ab1f24" }} role="status" />
          <p className="text-muted mt-2">Loading approval details...</p>
        </div>
      </div>
    );
  }

  if (!details && !loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <i className="ti ti-alert-circle text-danger fs-1 mb-2 d-block" />
          <h5>No Data Available</h5>
          <p className="text-muted">The requested self transfer approval details could not be found.</p>
          <button
            type="button"
            className="btn btn-outline-secondary mt-2"
            onClick={() => navigate(-1)}
          >
            Back to Worklist
          </button>
        </div>
      </div>
    );
  }

  const rawStatus = details.Status || "INPROCESS";
  const isCompleted =
    rawStatus === "APPROVE" ||
    rawStatus === "APPROVED" ||
    rawStatus === "A" ||
    rawStatus === "REJECT" ||
    rawStatus === "REJECTED" ||
    rawStatus === "R";

  const badge = resolveStatusBadge(rawStatus);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Self Transfer Approval</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.todoWorklist.tasks}>Worklist</Link>
                </li>
                <li className="breadcrumb-item active">Approval ({requestID})</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left me-1" />
              Back
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div
          className="card border-0 shadow-sm mb-4"
          style={{
            backgroundColor: isCompleted ? "#F0FDF4" : "#FFFBEB",
            borderLeft: `4px solid ${isCompleted ? "#22C55E" : "#F59E0B"}`,
          }}
        >
          <div className="card-body p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-semibold text-dark">Request Status:</span>
              <span className={badge.badgeClass} style={{ fontSize: "0.85rem", padding: "6px 14px" }}>
                <span
                  className="d-inline-block rounded-circle me-1"
                  style={{ width: 8, height: 8, backgroundColor: badge.dotColor }}
                />
                {badge.label}
              </span>
            </div>
            {workItemId && (
              <span className="text-muted small">
                Work Item ID: <strong className="text-dark font-monospace">{workItemId}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Section 1: Request Details */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 me-2"
              style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
            >
              <i className="ti ti-file-text text-danger" style={{ color: "#ab1f24" }} />
            </div>
            <h5 className="card-title mb-0 fw-bold text-dark">Request Details</h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <InfoRow label="Request ID" value={details.RequestID} />
              <InfoRow label="Transfer Type" value="Self Transfer" />
              <InfoRow label="Transfer Ground" value={groundLabel(details.TransferGround)} />
              <InfoRow label="Reason for Transfer" value={reasonLabel(details.ReasonForTransfer)} />
              {(details.ReasonForTransfer === "TEM" ||
                reasonLabel(details.ReasonForTransfer) === "Temporary") &&
                Number(details.TransferPeriod) > 0 && (
                <InfoRow
                  label="Transfer Period"
                  value={`${details.TransferPeriod} ${
                    String(details.YearMonthDays || "").toUpperCase().startsWith("Y")
                      ? "Year(s)"
                      : String(details.YearMonthDays || "").toUpperCase().startsWith("M")
                      ? "Month(s)"
                      : String(details.YearMonthDays || "").toUpperCase().startsWith("D")
                      ? "Day(s)"
                      : details.YearMonthDays || ""
                  }`}
                />
              )}
              <div className="col-12">
                <label className="form-label text-muted small mb-1">Remarks</label>
                <div className="p-3 bg-light rounded-3 text-dark">
                  {details.Remarks || "No remarks provided"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Employee Details */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 me-2"
              style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
            >
              <i className="ti ti-user text-danger" style={{ color: "#ab1f24" }} />
            </div>
            <h5 className="card-title mb-0 fw-bold text-dark">Employee Details</h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <InfoRow label="Employee Code" value={details.EmpID} />
              <InfoRow label="Name" value={details.EmployeeName} />
              <InfoRow label="Category" value={details.Category || "Executive"} />
              <InfoRow label="Pay Scale" value={details.PayScale} />
              <InfoRow
                label="Designation"
                value={details.DesignationNameOfPostHeldPresently || details.Designation}
              />
              <InfoRow
                label="Division / Unit"
                value={
                  details.DivisionUnitOfficeInWhichTheEmployee || details.Division
                }
              />
              <InfoRow label="HOD Unit" value={details.Designation} hideEmpty />
            </div>
          </div>
        </div>

        {/* Section 3: Transfer Destination */}
        {(details.TransferUnder || details.TransferUnderDesignation || details.NameOfHODUnit) && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-map-pin text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Transfer Destination</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <InfoRow label="HOD / Unit" value={details.NameOfHODUnit} />
                <InfoRow label="Transfer Under" value={details.TransferUnder} hideEmpty />
                <InfoRow
                  label="Transfer Under Designation"
                  value={details.TransferUnderDesignation}
                  hideEmpty
                />
              </div>
            </div>
          </div>
        )}

        {/* Section 4: Approvers Timeline */}
        {approvers.length > 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-users text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">
                Approvers ({approvers.length})
              </h5>
            </div>
            <div className="card-body p-4">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "70px" }}>Level</th>
                      <th>Approver</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvers.map((a, index) => {
                      const isPassedDown =
                        index === 0 ||
                        approvers.slice(0, index).every((prev) => {
                          const ps = String(prev.status || "").trim().toUpperCase();
                          return ["APPROVE", "APPROVED", "A"].includes(ps);
                        });
                      const s = String(a.status || "").trim().toUpperCase();
                      const displayStatus =
                        ["APPROVE", "APPROVED", "A"].includes(s)
                          ? "Approved"
                          : ["REJECT", "REJECTED", "R"].includes(s)
                          ? "Rejected"
                          : isPassedDown
                          ? "In Process"
                          : "Pending";
                      const b = resolveStatusBadge(displayStatus);
                      return (
                        <tr key={a.id}>
                          <td>
                            <span className="badge rounded-pill bg-light text-dark border fw-bold px-2 py-1">
                              L{a.level}
                            </span>
                          </td>
                          <td>
                            <div className="fw-semibold text-dark">{a.name}</div>
                            <small className="text-muted font-monospace">{a.employeeCode}</small>
                          </td>
                          <td>
                            <span className={b.badgeClass} style={{ fontSize: "0.75rem" }}>
                              {displayStatus}
                            </span>
                          </td>
                          <td className="small text-muted">{a.date || "—"}</td>
                          <td className="small text-muted text-break" style={{ maxWidth: 200 }}>
                            {a.remark || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Section 5: Take Action (Only when NOT completed) */}
        {!isCompleted && (
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-checklist text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Take Action</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                {/* Nodal Remarks */}
                <div className="col-12">
                  <label className="form-label fw-semibold small">
                    Nodal Remarks Regarding Availability of Vacancy and Feasibility of Transfer
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter nodal remarks..."
                    value={nodalRemarks}
                    onChange={(e) => setNodalRemarks(e.target.value)}
                  />
                </div>

                {/* Special Comment */}
                <div className="col-12">
                  <label className="form-label fw-semibold small">Comment</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter special comment (if any)..."
                    value={specialComment}
                    onChange={(e) => setSpecialComment(e.target.value)}
                  />
                </div>

                {/* Approver Comment */}
                <div className="col-12">
                  <label className="form-label fw-semibold small">Approver Comment</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter your comment..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>

                {/* Acknowledgement Checkbox */}
                <div className="col-12 mt-3">
                  <div className="form-check p-3 bg-light rounded-3 d-flex align-items-center gap-2">
                    <input
                      className="form-check-input ms-0 mt-0"
                      type="checkbox"
                      id="ackCheck"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      style={{ width: 18, height: 18, cursor: "pointer" }}
                    />
                    <label
                      className="form-check-label text-dark fw-medium small mb-0 cursor-pointer"
                      htmlFor="ackCheck"
                      style={{ cursor: "pointer" }}
                    >
                      Aforesaid comments have been seen &amp; noted
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12 mt-4 d-flex justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-danger px-4 py-2 d-flex align-items-center"
                    onClick={() => {
                      if (!acknowledged) {
                        toast.warning("Please check the acknowledgement checkbox before proceeding.");
                        return;
                      }
                      setConfirmModalAction("R");
                    }}
                    disabled={submitting}
                  >
                    <i className="ti ti-x me-1" />
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-success px-4 py-2 d-flex align-items-center text-white"
                    onClick={() => {
                      if (!acknowledged) {
                        toast.warning("Please check the acknowledgement checkbox before proceeding.");
                        return;
                      }
                      setConfirmModalAction("A");
                    }}
                    disabled={submitting}
                  >
                    <i className="ti ti-check me-1" />
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModalAction && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header border-bottom py-3 px-4">
                  <h6 className="modal-title fw-bold text-dark mb-0">
                    {confirmModalAction === "A" ? "Approve Request" : "Reject Request"}
                  </h6>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => !submitting && setConfirmModalAction(null)}
                  />
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="my-2">
                    <div
                      className={`d-inline-flex align-items-center justify-content-center rounded-circle ${confirmModalAction === "A" ? "bg-success-subtle" : "bg-danger-subtle"
                        }`}
                      style={{ width: 64, height: 64 }}
                    >
                      <i
                        className={`ti ${confirmModalAction === "A" ? "ti-check text-success" : "ti-x text-danger"
                          } fs-1`}
                      />
                    </div>
                  </div>
                  <h5 className="fw-bold text-dark mb-2 mt-3">
                    {confirmModalAction === "A"
                      ? "Are you sure you want to approve this Self Transfer request?"
                      : "Are you sure you want to reject this Self Transfer request?"}
                  </h5>
                  <p className="text-muted small mb-0">
                    This action will update the workflow status and notify subsequent approvers / the applicant.
                  </p>
                </div>
                <div className="modal-footer border-top py-3 px-4 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={() => setConfirmModalAction(null)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn px-4 text-white fw-semibold ${confirmModalAction === "A" ? "btn-success" : "btn-danger"
                      }`}
                    onClick={() => handleAction(confirmModalAction)}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Processing...
                      </>
                    ) : confirmModalAction === "A" ? (
                      "Approve"
                    ) : (
                      "Reject"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfTransferApproval;
